import type { ReactNode } from 'react'

import { formatCurrency, majorUnits } from '@/design'

import type { DescribeReason, NotAvailable, ReasonRule } from './nullState'
import './list.css'

/* The cell pieces every Analytics list shares: an undefined figure, a missing one, money and days. */

/** A figure undefined for its row (no orders, so no AOV): a dash, never a 0. */
export const UNDEFINED = <span className="list-muted">—</span>

/**
 * A figure the API could not give for this row: a dash, `label` for a screen reader
 * ("Not live yet", "Not synced yet"), and why on hover.
 */
export function Unavailable({ label, why }: { label: string; why: string }) {
  return (
    <span className="list-muted list-not-live" title={`${label}. ${why}`}>
      <span aria-hidden="true">—</span>
      <span className="ci-visually-hidden">{label}</span>
    </span>
  )
}

/** How money reads: the list's currency (`meta.currency`), in the tenant's locale (absent: the app's default). */
export interface MoneyFormat {
  currency: string
  locale?: string
}

/** How days read: the tenant's timezone, whose calendar days the dates show, in its locale. */
export interface DayFormat {
  timezone: string
  locale?: string
}

/** What every list's cells need beyond the row: which nulls are missing data, and how money and days read. */
export interface ListCellContext<Row, Reason extends string> extends MoneyFormat, DayFormat {
  notAvailable: NotAvailable<Row, Reason>
}

/** An amount in the list's currency. */
export function money(minor: number, { currency, locale }: MoneyFormat): string {
  return formatMinor(minor, currency, locale)
}

/** An instant as the tenant's calendar day, in the tenant's locale (as money is). */
export function day(instant: string, { timezone, locale }: DayFormat): string {
  return formatDay(instant, timezone, locale)
}

/** Integer minor units of `currency`, in the tenant's locale. */
export function formatMinor(minor: number, currency: string, locale: string | undefined): string {
  return formatCurrency(majorUnits(minor, currency), { currency, locale })
}

/** An instant as the tenant's calendar day, in the tenant's locale (as money is). */
export function formatDay(instant: string, timezone: string, locale: string | undefined): string {
  return new Date(instant).toLocaleDateString(locale ?? 'en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: timezone,
  })
}

/**
 * A list's nullable cells, bound to its one null rule and to how each reason reads:
 * `missing` says why a row's null in a field is missing data (a reasoned dash) or
 * undefined for that row (a plain dash), and `figure` makes a column's cell that formats
 * a value with `show`, or says why there is none.
 */
export function nullableCells<Row, Reason extends string, Context extends ListCellContext<Row, Reason>>(
  rule: ReasonRule<Row, Reason>,
  describe: DescribeReason<Reason>,
) {
  const missing = (row: Row, field: keyof Row, context: Context): ReactNode => {
    const reason = rule(row, field, context.notAvailable)
    if (!reason) return UNDEFINED
    const { label, why } = describe(reason)
    return <Unavailable label={label} why={why} />
  }
  const figure =
    <V,>(field: keyof Row, read: (row: Row) => V | null, show: (value: V, context: Context) => ReactNode) =>
    (row: Row, context: Context): ReactNode => {
      const value = read(row)
      return value === null ? missing(row, field, context) : show(value, context)
    }
  return { missing, figure }
}
